import type { FC, Ref } from "react";
import { useRef } from "react";
import { useBoolean } from "usehooks-ts";

import { COURSE_ID_QUERY_PARAM } from "components/constants";
import { SidebarOptionsMenu, SidebarOptionsMenuProps } from "components/menus/SidebarOptionsMenu";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { IconText } from "mds/components/IconText";
import { MenuDivider, MenuInstance, MenuItem, SubMenu } from "mds/components/Menu";
import { CopySendArrowIcon, FolderIcon, PencilEditIcon } from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi } from "store/index";
import { courseGroupingSelectors, courseSelectors } from "store/selectors/base";
import { selectSortedTermSectionsByCourse } from "store/selectors/section";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";
import { ADMIN_COURSE_EVENT_PREFIX } from "utils/amplitude";
import { useQueryParamsActions } from "utils/routing";

interface CourseOptionsMenuProps extends SidebarOptionsMenuProps {
  courseId: string;
  onRename: () => void;
}

export const CourseOptionsMenu: FC<CourseOptionsMenuProps> = ({
  courseId,
  onRename,
  className,
  kind,
}) => {
  const course = useObjectSelector(
    (state) => courseSelectors.selectById(state, courseId),
    ["id", "code", "title", "grouping_id"],
  );
  const menuRef: Ref<MenuInstance> = useRef(null);
  const { canAuthorOrg } = useCapabilities();
  const groupings = useListSelector(courseGroupingSelectors.selectAll, ["id", "title"]);
  const termSections = useListSelector(
    (s) => selectSortedTermSectionsByCourse(s, courseId),
    ["sections"],
  );
  const hasSections = termSections.some((term) => term.sections.length > 0);
  const filteredGroupings = groupings.filter((grouping) => grouping.id !== course?.grouping_id);
  const { deleteQueryParams } = useQueryParamsActions();

  const {
    value: isDeleteDialogOpen,
    setTrue: openDeleteDialog,
    setFalse: closeDeleteDialog,
  } = useBoolean(false);

  if (!course) {
    return null;
  }

  const handleMoveToGrouping = async (groupingId: string | null) => {
    await storeApi.courses.partial_update({
      id: courseId,
      grouping_id: groupingId,
    });

    toastSuccessMessage(t("home.toasts.course_moved_one"));
    menuRef.current?.closeMenu();
  };

  const handleDelete = async () => {
    await storeApi.courses.destroy(courseId);

    toastSuccessMessage(t("home.toasts.course_deleted"));

    deleteQueryParams([COURSE_ID_QUERY_PARAM]);
  };

  const moveToSubMenu = (
    <SubMenu
      disabled={!canAuthorOrg}
      label={
        <IconText iconStart={<CopySendArrowIcon />} text={t("sidebar_options_menu.move_to")} />
      }
      overflow="auto"
    >
      <div className="m-2 min-w-[200px]">
        <div className="pb-2 text-black-tint-40">{t("sidebar_options_menu.move_course_to")}</div>

        {filteredGroupings.map((grouping) => (
          <MenuItem key={grouping.id} onClick={() => handleMoveToGrouping(grouping.id)}>
            <IconText iconStart={<FolderIcon />} text={grouping.title} />
          </MenuItem>
        ))}

        {course.grouping_id && (
          <MenuItem onClick={() => handleMoveToGrouping(null)}>
            {t("sidebar_options_menu.uncategorized")}
          </MenuItem>
        )}
      </div>
    </SubMenu>
  );

  return (
    <>
      <SidebarOptionsMenu
        className={className}
        direction="bottom"
        eventPrefix={ADMIN_COURSE_EVENT_PREFIX}
        instanceRef={menuRef}
        kind={kind}
        optionsMenuActions={{
          onDelete: openDeleteDialog,
          isDeleteDisabled: hasSections || !canAuthorOrg,
          deleteTooltipText: hasSections
            ? t("sidebar_options_menu.cannot_delete_course_with_sections")
            : !canAuthorOrg
              ? t("sidebar_options_menu.no_delete_course_pemission")
              : undefined,
        }}
      >
        <MenuItem onClick={onRename}>
          <IconText iconStart={<PencilEditIcon />} text={t("home.courses.rename_course_heading")} />
        </MenuItem>

        <MenuDivider />

        {moveToSubMenu}
      </SidebarOptionsMenu>

      <ConfirmDeletionDialog
        glossaryKey="course"
        instanceName={course.code}
        open={isDeleteDialogOpen}
        onClose={closeDeleteDialog}
        onDelete={handleDelete}
      />
    </>
  );
};
